<template>
  <div class="invitations-tab">
    <section class="panel">
      <div class="panel-header">
        <div>
          <h3>Create invitation</h3>
          <p>The recipient must already have a LearningFirst account. Intake must be set to invite only.</p>
        </div>
      </div>
      <el-alert
        title="Creating an invitation does not send any email or message."
        description="You get a one-time link to share with the recipient yourself through a private channel. Only the invited account can claim it."
        type="info"
        show-icon
        :closable="false"
        class="panel-alert"
      />
      <el-form
        ref="createForm"
        :model="createForm"
        :rules="rules"
        :inline="true"
        size="small"
        @submit.native.prevent="createInvitation"
      >
        <el-form-item label="Recipient email" prop="user_email">
          <el-input v-model.trim="createForm.user_email" type="email" autocomplete="off" placeholder="partner@example.com" style="width: 280px" />
        </el-form-item>
        <el-form-item label="Expires after (days)">
          <el-input-number v-model="createForm.expires_days" :min="1" :max="30" :step="1" step-strictly controls-position="right" placeholder="14" style="width: 120px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" native-type="submit" :loading="isPending('create')" :disabled="anyPending">Create invitation link</el-button>
        </el-form-item>
      </el-form>
    </section>

    <section class="panel">
      <div class="panel-header">
        <div>
          <h3>Invitations</h3>
          <p>Latest 100 invitations. Invitation links cannot be shown again; revoke and create a new one if a link was lost.</p>
        </div>
        <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="fetchData">Refresh</el-button>
      </div>
      <el-table v-loading="loading" :data="items" border size="small" style="width: 100%" empty-text="No invitations yet">
        <el-table-column label="Invitation" min-width="160">
          <template slot-scope="scope">
            <span class="mono" :title="idOf(scope.row)">{{ shortId(scope.row, 12) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Recipient" min-width="230">
          <template slot-scope="scope">
            <div>{{ scope.row.user_email || 'Email unavailable' }}</div>
            <div class="muted mono">{{ scope.row.user_id || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Status" width="120">
          <template slot-scope="scope">
            <el-tag size="small" :type="statusTag(scope.row)">{{ statusLabel(scope.row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Created" width="150">
          <template slot-scope="scope">{{ formatTime(scope.row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="Expires" width="150">
          <template slot-scope="scope">{{ formatTime(scope.row.expires_at) }}</template>
        </el-table-column>
        <el-table-column label="Claimed / revoked" width="150">
          <template slot-scope="scope">{{ formatTime(scope.row.claimed_at || scope.row.revoked_at) }}</template>
        </el-table-column>
        <el-table-column label="Actions" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.status === 'issued'"
              size="mini"
              type="danger"
              plain
              :loading="isPending(`revoke:${idOf(scope.row)}`)"
              :disabled="anyPending"
              @click="revokeInvitation(scope.row)"
            >
              Revoke
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <el-dialog
      title="Invitation link"
      :visible.sync="linkVisible"
      width="640px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      @closed="clearCreated"
    >
      <template v-if="created">
        <el-alert
          title="This link is shown only once."
          description="LearningFirst stores only a hash of the token, so the link cannot be displayed again after you close this dialog. Nothing was sent to the recipient."
          type="warning"
          show-icon
          :closable="false"
          class="panel-alert"
        />
        <el-descriptions :column="1" size="small" border class="link-details">
          <el-descriptions-item label="Recipient">{{ created.user_email || created.user_id }}</el-descriptions-item>
          <el-descriptions-item label="User ID"><span class="mono">{{ created.user_id }}</span></el-descriptions-item>
          <el-descriptions-item label="Expires">{{ formatTime(created.expires_at) }}</el-descriptions-item>
        </el-descriptions>
        <el-input :value="created.link" readonly class="link-input">
          <el-button slot="append" icon="el-icon-document-copy" @click="copyText(created.link, 'Invitation link copied')">Copy</el-button>
        </el-input>
      </template>
      <span slot="footer">
        <el-button type="primary" @click="linkVisible = false">I have saved the link</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  createAffiliateInvitation,
  listAffiliateInvitations,
  revokeAffiliateInvitation
} from '@/apis/lfAdmin'
import affiliateMixin from './affiliateMixin'

const PUBLIC_SITE_ORIGIN = 'https://learningfirst.io'

export default {
  name: 'AffiliateInvitationsTab',
  mixins: [affiliateMixin],
  data() {
    return {
      loading: false,
      items: [],
      createForm: {
        user_email: '',
        expires_days: 14
      },
      rules: {
        user_email: [
          { required: true, message: 'Enter the recipient email.', trigger: 'blur' },
          { type: 'email', message: 'Enter a valid email.', trigger: 'blur' }
        ]
      },
      linkVisible: false,
      created: null
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    isExpired(row) {
      return row.status === 'issued' && Number(row.expires_at || 0) > 0 && Number(row.expires_at) * 1000 < Date.now()
    },
    statusLabel(row) {
      if (this.isExpired(row)) return 'Expired'
      const map = { issued: 'Issued', claimed: 'Claimed', revoked: 'Revoked' }
      return map[row.status] || row.status || '-'
    },
    statusTag(row) {
      if (this.isExpired(row)) return 'info'
      if (row.status === 'claimed') return 'success'
      if (row.status === 'revoked') return 'danger'
      return ''
    },
    async fetchData() {
      this.loading = true
      const data = await this.loadData(() => listAffiliateInvitations(), 'Failed to load invitations')
      if (data !== undefined) this.items = Array.isArray(data) ? data : []
      this.loading = false
    },
    createInvitation() {
      this.$refs.createForm.validate(async (valid) => {
        if (!valid) return
        const email = this.createForm.user_email.toLowerCase()
        const days = this.createForm.expires_days
        const params = { user_email: email }
        if (Number.isInteger(days)) params.expires_days = days
        const confirmed = await this.confirmAction({
          title: 'Create invitation',
          lines: [
            `Create a one-time affiliate invitation for ${email}?`,
            `The link expires after ${params.expires_days || 14} days. No email or message will be sent; you share the link yourself.`
          ],
          confirmText: 'Create link',
          type: 'info'
        })
        if (!confirmed) return
        const result = await this.runMutation('create', () => createAffiliateInvitation(params), {
          failure: 'Failed to create invitation'
        })
        if (!result || !result.data) return
        const data = result.data
        this.created = {
          link: `${PUBLIC_SITE_ORIGIN}${data.relative_path || ''}`,
          user_email: data.user_email,
          user_id: data.user_id,
          expires_at: data.expires_at
        }
        this.linkVisible = true
        this.createForm.user_email = ''
        this.$nextTick(() => {
          if (this.$refs.createForm) this.$refs.createForm.clearValidate()
        })
        this.fetchData()
      })
    },
    clearCreated() {
      // Drop the raw token from memory once the admin closes the dialog.
      this.created = null
    },
    async revokeInvitation(row) {
      const id = this.idOf(row)
      const confirmed = await this.confirmAction({
        title: 'Revoke invitation',
        lines: [
          `Revoke the unclaimed invitation for ${row.user_email || `user ${row.user_id}`}?`,
          'The link stops working immediately. This cannot be undone.'
        ],
        confirmText: 'Revoke',
        type: 'error'
      })
      if (!confirmed) return
      const result = await this.runMutation(`revoke:${id}`, () => revokeAffiliateInvitation({ invitation_id: id }), {
        success: 'Invitation revoked',
        failure: 'Failed to revoke invitation'
      })
      if (result) this.fetchData()
    }
  }
}
</script>

<style scoped>
.panel {
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #ebeef5;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.panel-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.panel-header p {
  margin: 4px 0 0;
  color: #909399;
  font-size: 13px;
}

.panel-alert {
  margin-bottom: 12px;
}

.muted {
  color: #909399;
  font-size: 12px;
}

.mono {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  word-break: break-all;
}

.link-details {
  margin-bottom: 12px;
}

.link-input >>> input {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
}
</style>
